import get from 'lodash/get'

import { CA_KEY } from '@/constants'
import { generateCode, UpdateDeepPartial } from '@/store/utils'
import { ItemType } from '@/types'
import { formatCodeByCase } from '@/utils'

import {
	AddEmptyRowOptions,
	CreateUpdateOptions,
	HandleRowCompletionOptions,
	UpdateTableAndPropertiesValuesOptions,
} from './types'

export const findOriginalIndex = (
	fieldId: number,
	uuid: string | undefined,
	items: ItemType[],
	itemsKey: string,
	rowIndex: number,
): number => {
	if (itemsKey === 'columns' && uuid) {
		return (items || []).findIndex((item) => {
			return item['uuid'] === uuid
		})
	}

	if (itemsKey === 'columns' && !uuid) {
		return rowIndex
	}

	return (items || []).findIndex((item) => {
		return item['id'] === fieldId
	})
}
// addCompleteRow - an updated helper function
const addNewRow = ({
	createDynamicEmptyRow,
	data,
	idCounterKey,
	idKey,
	items,
	originalIndex,
	update,
	updatedRow,
}: AddEmptyRowOptions) => {
	updatedRow[originalIndex] = {
		...createDynamicEmptyRow(items),
		...updatedRow[originalIndex],
	}
	// Generate new ID
	const newId = data[idCounterKey] + 1

	updatedRow[originalIndex][idKey] = newId

	update[idCounterKey] = newId
}

// handleRowCompletion - an updated helper function
const handleRowCompletion = ({
	createDynamicEmptyRow,
	data,
	idCounterKey,
	idKey,
	items,
	itemsKey,
	originalIndex,
	update,
}: HandleRowCompletionOptions) => {
	const updatedRow = update[itemsKey] as UpdateDeepPartial<ItemType>
	const updatedItemId = updatedRow[originalIndex]?.id

	const isUpdate = data[itemsKey].find(
		(dataItem: any) => dataItem.id === updatedItemId && updatedItemId !== -1,
	)

	if (isUpdate) {
		return
	}

	return addNewRow({
		createDynamicEmptyRow,
		data,
		idCounterKey,
		idKey,
		items,
		update,
		updatedRow,
		originalIndex,
	})
}

// createUpdate - a new helper function
const createUpdate = async ({
	createDynamicEmptyRow,
	data,
	dispatch,
	fieldId,
	fieldName,
	fieldValue,
	idCounterKey,
	idKey,
	items,
	itemsKey,
	originalIndex,
	isCodeLowerCase,
	fieldOptions,
	properties,
}: CreateUpdateOptions): Promise<UpdateDeepPartial<any>> => {
	const codeProperties = properties.find(
		(property) => property.field === 'code',
	)
	const canGenerateCodeFieldName = codeProperties?.canGenerateCodeFieldName

	const getCodeProperty = (
		code: string,
		isCodeLowerCase: boolean,
		canGenerateCodeFieldName: boolean,
	) => {
		if (!canGenerateCodeFieldName) {
			return {}
		}
		return { code: formatCodeByCase(code, isCodeLowerCase) }
	}

	const getCode = async () => {
		const lastCodeValue = get(items[originalIndex], 'code')

		if (!canGenerateCodeFieldName) {
			return lastCodeValue
		}

		if (fieldName === 'name') {
			const response = await dispatch(generateCode(fieldValue))
			return response.result
		}

		if (fieldName === 'code') {
			return fieldValue
		}

		return lastCodeValue
	}

	const code = await getCode()

	const update: UpdateDeepPartial<any> = {
		[itemsKey]: {
			[originalIndex]: {
				[idKey]: fieldId,
				[fieldName]: fieldValue,
				...getCodeProperty(code, isCodeLowerCase, canGenerateCodeFieldName),
			},
		},
	}

	handleRowCompletion({
		createDynamicEmptyRow,
		data,
		idCounterKey,
		idKey,
		items,
		itemsKey,
		fieldName,
		originalIndex,
		update,
		fieldValue,
		fieldId,
	})

	if (fieldOptions?.isCustomAttribute) {
		const updateCA: UpdateDeepPartial<any> = {
			[itemsKey]: {
				[originalIndex]: {
					[CA_KEY]: fieldValue,
				},
			},
		}

		return updateCA
	}

	return update
}

export const updateTableAndPropertiesValues = async ({
	createDynamicEmptyRow,
	data,
	dispatch,
	idCounterKey,
	idKey,
	items,
	itemsKey,
	fieldName,
	onChange,
	originalIndex,
	setLastEditedCell,
	fieldValue,
	fieldId,
	isCodeLowerCase,
	rowIndex,
	fieldOptions,
	properties,
}: UpdateTableAndPropertiesValuesOptions) => {
	const update = await createUpdate({
		createDynamicEmptyRow,
		data,
		dispatch,
		idCounterKey,
		idKey,
		items,
		itemsKey,
		fieldId,
		fieldName,
		originalIndex,
		fieldValue,
		isCodeLowerCase,
		fieldOptions,
		properties,
	})

	setLastEditedCell({ rowId: rowIndex, fieldName })
	onChange(update, fieldName)
}
